import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import '../styles/MediaPlayerStyles.css';
import '../styles/MasterclassPlayer.css';

import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';

import { HeaderNavigation } from '../../shared/headerNavigation/HeaderNavigation';
import WhitePopUpWithLinks from '../../shared/modal/whitePopUp/WhitePopUpWithLinks';
import { initialLocationKey } from '../../shared/util/enum';
import { setMasterclass, setSingleMedia } from '../mediaSlice';
import { McMediaDescription } from './McMediaDescription';
import { ProgrammeChapters } from './ProgrammeChapters';
import SingleMediaPlayer from './SingleMediaPlayer';

export function ProgrammePlayer() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const day = window.location.pathname.split('/')[4]?.slice(4);
  const chapterNoRef = useRef(0);
  const [openCountdown, setOpenCountdown] = useState(false);
  const [keepPlaying, setKeepPlaying] = useState(false);
  const [progCompletedOpen, setProgCompletedOpen] = useState(false);
  const [showDayCompletedPopup, setShowDayCompletedPopup] = useState(false);
  const location = useLocation();
  const { pathname } = location;
  const mainProgrammePath = pathname.replace(/\/day_\d+$/, '');

  const programme = useSelector((state) => state.media.programme);
  const activeDay = useSelector((state) => state.media.masterclass);
  const nowActive = useSelector((state) => state.media.singleMedia);

  // scroll top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // set active day(masterclass) from url
  useEffect(() => {
    if (programme) {
      dispatch(setMasterclass(programme.days[Number(day) - 1]));
    }
  }, [programme, day]);

  // set active chapter to play
  useEffect(() => {
    if (
      activeDay &&
      (!nowActive._id ||
        chapterNoRef.current === 0 ||
        !activeDay.chapters.some((chapter) => chapter._id === nowActive._id))
    ) {
      // if all values in progress array are 0 or 100 play first chapter
      if (
        activeDay.progress_array.every((item) => item === 0) ||
        activeDay.progress_array.every((item) => Math.ceil(item) === 100)
      ) {
        dispatch(setSingleMedia(activeDay.chapters[0]));
        chapterNoRef.current = 1;
      } else {
        const started = activeDay.progress_array.findIndex(
          (item) => item > 0 && Math.ceil(item) < 100
        );
        if (started !== -1) {
          dispatch(setSingleMedia(activeDay.chapters[started]));
          chapterNoRef.current = started + 1;
        } else {
          // find last completed chapter
          let lastCompletedIndex;
          for (let i = 0; i < activeDay.progress_array.length; i++) {
            if (Math.ceil(activeDay.progress_array[i]) === 100) {
              lastCompletedIndex = i;
            }
          }
          if (lastCompletedIndex >= 0 && lastCompletedIndex < activeDay.progress_array.length - 1) {
            dispatch(setSingleMedia(activeDay.chapters[lastCompletedIndex + 1]));
            chapterNoRef.current = lastCompletedIndex + 2;
          }
        }
      }
    }
  }, [activeDay, keepPlaying]);

  // play chapter chosen from the chapters list
  const handleSetActive = (item) => {
    const index = activeDay.chapters.findIndex((chapter) => chapter._id === item._id);
    dispatch(setSingleMedia(item));
    chapterNoRef.current = index + 1;
  };

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      sessionStorage.setItem('pageReload', true);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const handleNavigation = () => {
    navigate(-1);
  };

  // functions related to autoplay
  const handleProgAutoPlay = () => {
    // setKeepPlaying(false);
    if (chapterNoRef.current < activeDay.chapters.length) {
      setOpenCountdown(true);
    } else if (day === programme.days.length.toString()) {
      setProgCompletedOpen(true);
    } else {
      setShowDayCompletedPopup(true);
    }
  };

  useEffect(() => {
    if (nowActive?.chid) {
      const mcMedia = document.getElementsByClassName('mcplayer');
      const text = document.getElementsByClassName('below-video');
      if (mcMedia[0]) {
        mcMedia[0].classList.add('fade-in');
        text[0].classList.add('fade-in');
      }
    }
  }, [nowActive?.chid]);

  const playNext = () => {
    const mcMedia = document.getElementsByClassName('mcplayer');
    const text = document.getElementsByClassName('below-video');
    if (mcMedia[0] && text[0]) {
      mcMedia[0].classList.remove('fade-in');
      text[0].classList.remove('fade-in');
    }
    setKeepPlaying(true);
    setTimeout(() => {
      dispatch(setSingleMedia(activeDay.chapters[chapterNoRef.current]));
      chapterNoRef.current += 1;
    }, 700);
  };

  // end of the programme
  const closeProgrammeCompleted = () => {
    setProgCompletedOpen(false);
    handleNavigation();
  };

  // setting player height, so other elements don't move around when chapters change
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [mediaHeight, setMediaHeight] = useState(0);

  const calculateHeight = () => {
    let newValue;
    if (screenWidth < 850) {
      newValue = Math.floor(screenWidth * 0.6);
    } else {
      newValue = Math.floor(screenWidth * 0.38);
    }
    setMediaHeight(newValue);
  };

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [screenWidth]);

  useEffect(() => {
    calculateHeight();
  }, [screenWidth]);

  const getEndProgrammePopUpContents = () => {
    return {
      paragraphs: [t('day.completed.popup.description')],
      backBtn: {
        text: t('back.button'),
        handleClick: () => {
          setShowDayCompletedPopup(false);
        },
      },
      continueBtn: {
        text: t('M012'),
        link: mainProgrammePath,
      },
    };
  };

  return (
    <>
      <HeaderNavigation
        bottomBarActionButton={
          location.key !== initialLocationKey && {
            ariaLabel: t('nav.button.back'),
            onClick: handleNavigation,
            icon: {
              faIconDefinition: faChevronLeft,
              size: 'lg',
            },
            variant: 'icon-only',
            className: 'back-button',
          }
        }
      />
      <div id="masterclass-container">
        <div className="column-1">
          <div className="media-cont" style={{ height: `${mediaHeight}px` }}>
            {showDayCompletedPopup && (
              <WhitePopUpWithLinks
                popUpContents={getEndProgrammePopUpContents()}
                dialogTitle={t('day.completed.popup.title')}
                enableEscape
              />
            )}
            <SingleMediaPlayer
              mediaHeight={mediaHeight}
              programme
              masterclass
              handleProgAutoPlay={handleProgAutoPlay}
              keepPlaying={keepPlaying}
              progressArray={activeDay.progress_array}
              nowActiveIndex={chapterNoRef.current - 1}
              openCountdown={openCountdown}
              setOpenCountdown={setOpenCountdown}
              playNext={playNext}
              progress={activeDay.progress_array[chapterNoRef.current - 1]}
              progCompletedOpen={progCompletedOpen}
              closeProgrammeCompleted={closeProgrammeCompleted}
            />
          </div>
          <div className="below-video">
            {nowActive && (
              <McMediaDescription
                masterclass={activeDay}
                nowActive={nowActive}
                chapterNo={chapterNoRef.current}
                day={day}
              />
            )}
          </div>
        </div>

        {nowActive && (
          <ProgrammeChapters
            masterclass={activeDay}
            handleSetActive={handleSetActive}
            nowActiveIndex={chapterNoRef.current - 1}
          />
        )}
      </div>
    </>
  );
}
